import { useEffect, useMemo, useRef } from 'react'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import { AnyAction } from 'redux'
import { ThunkDispatch } from 'redux-thunk'
import { State } from 'store'
import { TimeSlotGroup } from 'timeSlots/timeSlotsService'
import { timeSlotEndpoints } from 'timeSlots/reduxQuery'

export type GroupDetailStatus = 'loading' | 'ready' | 'error'

export interface GroupDetail {
    status: GroupDetailStatus
    group?: TimeSlotGroup
}

export function useTimeSlotGroupDetails(accountSlug: string, groupIds: string[]): Record<string, GroupDetail> {
    const dispatch = useDispatch<ThunkDispatch<State, unknown, AnyAction>>()
    const subscriptions = useRef<Record<string, { unsubscribe: () => void }>>({})
    const key = [...new Set(groupIds)].sort().join(',')

    useEffect(() => {
        const ids = key ? key.split(',') : []
        for (const groupId of ids) {
            if (!subscriptions.current[groupId]) {
                subscriptions.current[groupId] = dispatch(
                    timeSlotEndpoints.getTimeSlotGroup.initiate({ accountSlug, groupId }),
                )
            }
        }
    }, [accountSlug, key, dispatch])

    useEffect(
        () => () => {
            Object.values(subscriptions.current).forEach((subscription) => subscription.unsubscribe())
            subscriptions.current = {}
        },
        [accountSlug],
    )

    const ids = useMemo(() => (key ? key.split(',') : []), [key])
    const selectors = useMemo(
        () => ids.map((groupId) => timeSlotEndpoints.getTimeSlotGroup.select({ accountSlug, groupId })),
        [accountSlug, ids],
    )
    const results = useSelector((state: State) => selectors.map((select) => select(state)), shallowEqual)

    return useMemo(() => {
        const details: Record<string, GroupDetail> = {}
        ids.forEach((groupId, index) => {
            const result = results[index]
            if (result.isError) {
                details[groupId] = { status: 'error' }
            } else if (result.data) {
                details[groupId] = { status: 'ready', group: result.data }
            } else {
                details[groupId] = { status: 'loading' }
            }
        })
        return details
    }, [ids, results])
}
